// Pure GPS track processing for Orbit (Strava-style tracker) and anything that
// records a path of points. Distances use the haversine formula; everything is
// pure + unit-tested. Points are { latitude, longitude, timestamp? } (timestamp
// = epoch ms or ISO string), tolerant of lat/lng/lon aliases.

export interface TrackPoint {
  latitude?: number;
  longitude?: number;
  lat?: number;
  lng?: number;
  lon?: number;
  timestamp?: number | string;
  time?: number | string;
}

const R_M = 6371000; // earth radius in metres

function coordOf(p: TrackPoint): { lat: number; lng: number } | null {
  if (!p || typeof p !== 'object') return null;
  const lat = Number(p.latitude ?? p.lat);
  const lng = Number(p.longitude ?? p.lng ?? p.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function timeOf(p: TrackPoint): number | null {
  const t = p?.timestamp ?? p?.time;
  if (t == null) return null;
  const ms = typeof t === 'number' ? t : Date.parse(String(t));
  return Number.isFinite(ms) ? ms : null;
}

/** Great-circle distance between two points, in metres. */
export function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R_M * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/** Total path length over a list of points, in metres. */
export function trackDistanceMeters(points: TrackPoint[]): number {
  if (!Array.isArray(points)) return 0;
  let total = 0;
  let prev: { lat: number; lng: number } | null = null;
  for (const p of points) {
    const c = coordOf(p);
    if (!c) continue;
    if (prev) total += haversineMeters(prev, c);
    prev = c;
  }
  return total;
}

/**
 * Drop points closer than `minMeters` to the last kept point (noise reduction /
 * downsampling). Always keeps the first and (if distinct) last point.
 */
export function downsampleTrack(points: TrackPoint[], minMeters = 5): TrackPoint[] {
  if (!Array.isArray(points) || points.length === 0) return [];
  const out: TrackPoint[] = [];
  let last: { lat: number; lng: number } | null = null;
  for (const p of points) {
    const c = coordOf(p);
    if (!c) continue;
    if (!last || haversineMeters(last, c) >= minMeters) {
      out.push(p);
      last = c;
    }
  }
  // Ensure the final point is represented (end of the path matters).
  const lastValid = [...points].reverse().find((p) => coordOf(p));
  if (lastValid && out[out.length - 1] !== lastValid) out.push(lastValid);
  return out;
}

export interface TrackStats {
  distanceMeters: number;
  durationSeconds: number;
  /** seconds per kilometre (0 when distance/time unavailable) */
  paceSecPerKm: number;
  /** metres per second */
  speedMps: number;
}

/** Distance + duration + pace/speed for a track (needs timestamps for time). */
export function trackStats(points: TrackPoint[]): TrackStats {
  const distanceMeters = trackDistanceMeters(points);
  let durationSeconds = 0;
  if (Array.isArray(points) && points.length >= 2) {
    const times = points.map(timeOf).filter((t): t is number => t != null);
    if (times.length >= 2) durationSeconds = Math.max(0, (times[times.length - 1] - times[0]) / 1000);
  }
  const km = distanceMeters / 1000;
  const paceSecPerKm = km > 0 && durationSeconds > 0 ? durationSeconds / km : 0;
  const speedMps = durationSeconds > 0 ? distanceMeters / durationSeconds : 0;
  return { distanceMeters, durationSeconds, paceSecPerKm, speedMps };
}

export interface Split {
  index: number;       // 1-based km (or unit) number
  distanceMeters: number;
  durationSeconds: number;
  paceSecPerKm: number;
}

/**
 * Per-unit splits (default every 1000 m). Each split spans the points whose
 * cumulative distance falls in that bucket; needs timestamps for durations.
 */
export function trackSplits(points: TrackPoint[], unitMeters = 1000): Split[] {
  if (!Array.isArray(points) || points.length < 2) return [];
  const splits: Split[] = [];
  let cumulative = 0;
  let bucketStartDist = 0;
  let bucketStartTime: number | null = null;
  let prev: { lat: number; lng: number } | null = null;
  let prevTime: number | null = null;

  const pushSplit = (endDist: number, endTime: number | null) => {
    const dur = bucketStartTime != null && endTime != null ? Math.max(0, (endTime - bucketStartTime) / 1000) : 0;
    const dist = endDist - bucketStartDist;
    const km = dist / 1000;
    splits.push({
      index: splits.length + 1,
      distanceMeters: dist,
      durationSeconds: dur,
      paceSecPerKm: km > 0 && dur > 0 ? dur / km : 0,
    });
    bucketStartDist = endDist;
    bucketStartTime = endTime;
  };

  for (const p of points) {
    const c = coordOf(p);
    if (!c) continue;
    const t = timeOf(p);
    if (bucketStartTime == null) bucketStartTime = t;
    if (prev) {
      cumulative += haversineMeters(prev, c);
      while (cumulative - bucketStartDist >= unitMeters) {
        pushSplit(bucketStartDist + unitMeters, t ?? prevTime);
      }
    }
    prev = c;
    prevTime = t;
  }
  // trailing partial split
  if (cumulative - bucketStartDist > 1) pushSplit(cumulative, prevTime);
  return splits;
}
